
import {
    defineComponent,
    onMounted,
    ref,
    onBeforeUnmount,
    watch,
    computed,
    h,
} from '@vue/composition-api';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { AccountFlagNames } from '@modules/userProfile/account/FeatureFlags';
import { getConfig } from '@superbet-group/web.lib.config';
import { useVuexStore } from '@superbet-group/web.lib.vue-utilities';
import { AuthenticationStore } from '@store/modules/data/authentication/authenticationIndex';
import QuickDepositContainer from './QuickDepositContainer.vue';
import { errorGuard } from '@core/utils/ErrorGuard';
import maintenanceStoreIndex from '@store/modules/data/maintenance/maintenanceIndex';
import { DepositType } from '@core/models/user/enums';

export default defineComponent({
    props: {
        isHighlighted: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const isQuickDepositEnabled = ref(false);
        let featureSubscriptionUnsubscribeFn: () => void;
        const authModule = useVuexStore<AuthenticationStore>('data/authentication');
        const fetchDepositEligibility = authModule.action('fetchDepositEligibility');
        const userId = authModule.getter('userId');
        const depositEligibility = authModule.getter('depositEligibility');
        const countryHasIndividualEligibility
            = getConfig().countrySpecific.user.deposit.hasEligibility;

        const maintenanceStore = useVuexStore<typeof maintenanceStoreIndex>('data/maintenance');
        const isDepositTypeAvailable = maintenanceStore.getter('isDepositTypeAvailable');
        const isOnlinePaymentEnabled = computed(() => isDepositTypeAvailable.value(DepositType.Online) || isDepositTypeAvailable.value(DepositType.OnlinePix));

        onMounted(() => {
            featureSubscriptionUnsubscribeFn = featureFlagService.subscribeToFeatureChanges(AccountFlagNames.isQuickDepositEnabled, (value) => {
                isQuickDepositEnabled.value = value;
            }, false);
        });

        watch(isQuickDepositEnabled, async (value) => {
            if (value && countryHasIndividualEligibility) {
                await errorGuard(
                    async () => {
                        await fetchDepositEligibility();
                    },
                    {
                        onError: () => {},
                    },
                );
            }
        });

        onBeforeUnmount(() => {
            featureSubscriptionUnsubscribeFn?.();
        });

        const isOnlineDepositDisabled = computed(() => {
            return (
                (countryHasIndividualEligibility && !depositEligibility.value?.isOnlineEligible) || !isOnlinePaymentEnabled.value
            );
        });

        const isQrDepositShown = computed(() => {
            return userId.value && isQuickDepositEnabled.value && !isOnlineDepositDisabled.value;
        });

        return () => isQrDepositShown.value ? h(QuickDepositContainer, { props }) : h(undefined);
    },
});
